import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import calendarApi from '../../api/calendarApi';

interface CalendarIntegrationProps {
  onStatusChange?: (connected: boolean) => void;
}

const CalendarIntegration: React.FC<CalendarIntegrationProps> = ({ onStatusChange }) => {
  const { user } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);

  useEffect(() => {
    checkCalendarStatus();
  }, []);

  const checkCalendarStatus = async () => {
    try {
      setIsLoading(true);
      const data = await calendarApi.getStatus();
      setIsConnected(data.connected);
      
      if (onStatusChange) {
        onStatusChange(data.connected);
      }
    } catch (err) {
      console.error('Error checking calendar status:', err);
      setError('Failed to check calendar status');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      setIsLoading(true);
      setError('');

      const data = await calendarApi.getAuthUrl();
      window.location.href = data.authUrl;
      
    } catch (err) {
      console.error('Error connecting calendar:', err);
      setError('Failed to connect to Google Calendar');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      setIsLoading(true);
      setError('');

      await calendarApi.disconnect();
      setIsConnected(false);
      setShowDisconnectDialog(false);
      
      if (onStatusChange) {
        onStatusChange(false);
      }
      
    } catch (err) {
      console.error('Error disconnecting calendar:', err);
      setError('Failed to disconnect Google Calendar');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <CalendarIcon sx={{ mr: 2, color: 'primary.main' }} />
          <Typography variant="h6" component="h3">
            Google Calendar Integration
          </Typography>
          {isConnected && (
            <Chip
              icon={<CheckCircleIcon />}
              label="Connected"
              color="success"
              size="small"
              sx={{ ml: 2 }}
            />
          )}
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Sync your appointments with Google Calendar to keep track of your schedule across all devices.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {isLoading ? (
            <CircularProgress size={24} />
          ) : isConnected ? (
            <>
              <FormControlLabel
                control={<Switch checked={true} disabled />}
                label="Calendar sync enabled"
              />
              <Button
                variant="outlined"
                color="error"
                startIcon={<LinkOffIcon />}
                onClick={() => setShowDisconnectDialog(true)}
                size="small"
              >
                Disconnect
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              startIcon={<LinkIcon />}
              onClick={handleConnect}
              disabled={isLoading}
            >
              Connect Google Calendar
            </Button>
          )}
        </Box>

        {isConnected && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
            <Typography variant="body2" color="success.dark">
              ✅ Your appointments will automatically sync to your Google Calendar
            </Typography>
          </Box>
        )}
      </CardContent>

      {/* Disconnect Confirmation Dialog */}
      <Dialog
        open={showDisconnectDialog}
        onClose={() => setShowDisconnectDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Disconnect Google Calendar?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to disconnect your Google Calendar? 
            Future appointments will no longer sync to your calendar.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Note: Existing calendar events will not be removed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDisconnectDialog(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleDisconnect}
            color="error"
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={20} /> : 'Disconnect'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default CalendarIntegration;
